import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, Card, CardBody, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";
import { FiArrowRight, FiFileText, FiUser } from "react-icons/fi";

const STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  INFO_REQUESTED: "info_requested",
  REJECTED: "rejected",
};

const STATUS_META = {
  [STATUS.PENDING]: { label: "Pending", color: "#B45309", bg: "#FEF3C7" },
  [STATUS.APPROVED]: { label: "Application approved", color: "#047857", bg: "#D1FAE5" },
  [STATUS.INFO_REQUESTED]: { label: "Info requested", color: "#B45309", bg: "#FEF3C7" },
  [STATUS.REJECTED]: { label: "Application rejected", color: "#B91C1C", bg: "#FEE2E2" },
};

const INFO_REQUEST_REASONS = [
  "Registration certificate",
  "Address proof",
  "Identity proof",
  "Business details clarification",
  "Bank account details",
];

const REJECTION_REASONS = [
  "Incomplete application",
  "Invalid details",
  "Failed verification",
  "Duplicate application",
  "Outside service coverage",
];

const buildInitials = (name = "") => {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "S";
};

const PartnerApplicationDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    AuthReducer: { auth },
  } = useSelector((state) => state);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [partner, setPartner] = useState(null);
  const [applicationStatus, setApplicationStatus] = useState(STATUS.PENDING);
  const [internalNote, setInternalNote] = useState("");
  const [communicationHistory, setCommunicationHistory] = useState([]);

  const [approveModal, setApproveModal] = useState(false);
  const [infoModal, setInfoModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);

  const [infoReason, setInfoReason] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [rejectMessage, setRejectMessage] = useState("");

  const [historyOpen, setHistoryOpen] = useState(true);

  useEffect(() => {
    const fetchPartnerApplication = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/admin/v1/partner-details/${id}`, {
          headers: { Authorization: `Bearer ${auth?.token}` },
        });
        const data = res?.data?.partner || null;
        setPartner(data);
        setInternalNote(data?.internalNote || "");

        const isVerified = data?.isVerified;
        const reason = (data?.reason || "").toLowerCase();
        if (isVerified === 1) setApplicationStatus(STATUS.APPROVED);
        else if (isVerified === 2) setApplicationStatus(STATUS.REJECTED);
        else if (reason.includes("follow up") || reason.includes("review") || reason.includes("waiting")) {
          setApplicationStatus(STATUS.INFO_REQUESTED);
        } else {
          setApplicationStatus(STATUS.PENDING);
        }

        setCommunicationHistory([
          {
            id: "received",
            type: "Application received",
            text: "Application received — acknowledgement sent",
            email: data?.email || "applicant@email.com",
            time: data?.createdAt ? moment(data.createdAt).format("MMM D, h:mm A") : "Today",
          },
        ]);
      } catch {
        toast.error("Failed to load partner application details");
        setPartner(null);
      } finally {
        setLoading(false);
      }
    };

    if (id && auth?.token) fetchPartnerApplication();
  }, [id, auth?.token]);

  const meta = STATUS_META[applicationStatus];

  const topBadgeLabel = useMemo(() => {
    if (applicationStatus === STATUS.APPROVED) return "Approved";
    if (applicationStatus === STATUS.INFO_REQUESTED) return "Info Requested";
    if (applicationStatus === STATUS.REJECTED) return "Rejected";
    return "Pending";
  }, [applicationStatus]);

  const submitVerification = async (status, reason = "", remarks = "") => {
    if (!partner?.id) return;
    setSaving(true);
    try {
      await axios.post(
        "/api/admin/v1/verify-partner-registration",
        {
          partnerId: partner.id,
          status,
          reason,
          remarks,
        },
        { headers: { Authorization: `Bearer ${auth?.token}` } }
      );
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update application status");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const appendHistory = (text) => {
    setCommunicationHistory((prev) => [
      ...prev,
      {
        id: `${Date.now()}`,
        type: "System email",
        text,
        email: partner?.email || "applicant@email.com",
        time: "Just now",
      },
    ]);
  };

  const onApprove = async () => {
    const ok = await submitVerification(1, "Partner Approved", "Approved by admin");
    if (!ok) return;
    setApproveModal(false);
    setApplicationStatus(STATUS.APPROVED);
    appendHistory("Approval email sent — welcome to the platform & login credentials");
    toast.success("Application approved successfully");
  };

  const onInfoRequest = async () => {
    if (!infoReason) {
      toast.warn("Please select what is needed");
      return;
    }
    const ok = await submitVerification(0, infoReason, infoMessage);
    if (!ok) return;
    setInfoModal(false);
    setApplicationStatus(STATUS.INFO_REQUESTED);
    appendHistory(`Info request sent — ${infoReason}`);
    setInfoReason("");
    setInfoMessage("");
    toast.success("Information request sent");
  };

  const onReject = async () => {
    if (!rejectReason) {
      toast.warn("Please select rejection reason");
      return;
    }
    const ok = await submitVerification(2, rejectReason, rejectMessage);
    if (!ok) return;
    setRejectModal(false);
    setApplicationStatus(STATUS.REJECTED);
    appendHistory(`Rejection email sent — ${rejectReason}`);
    setRejectReason("");
    setRejectMessage("");
    toast.success("Application rejected");
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner />
      </div>
    );
  }

  if (!partner) {
    return <div className="text-center py-5">Application not found.</div>;
  }

  return (
    <div style={{ background: "#F3F4F6", margin: "-12px -12px 0", minHeight: "calc(100vh - 80px)", padding: "16px" }}>
      <div className="d-flex align-items-center gap-2 small mb-3 text-muted">
        <button type="button" className="btn btn-link p-0 text-decoration-none" onClick={() => navigate(-1)}>
          ← Back to Applications
        </button>
        <span>Partner Applications</span>
        <span>›</span>
        <strong className="text-dark">{partner?.registration_id || `APP-${id}`}</strong>
      </div>

      <Card
        className="mb-3"
        style={{ borderRadius: 12, border: "1px solid #E5E7EB", boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)" }}
      >
        <CardBody className="p-3">
          <div className="d-flex justify-content-between gap-3 flex-wrap align-items-start">
            <div className="d-flex gap-3 align-items-center">
              <div
                className="d-flex align-items-center justify-content-center fw-semibold"
                style={{ height: 40, width: 40, borderRadius: 8, background: "#DBEAFE", color: "#1D4ED8", fontSize: 14 }}
              >
                {buildInitials(partner?.businessName || partner?.fullName)}
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <h4 className="mb-0 text-dark fw-bold" style={{ fontSize: "2rem", lineHeight: 1.2 }}>
                  {partner?.businessName || "State Museum"}
                </h4>
                <span
                  className="d-inline-flex align-items-center px-2 py-1 rounded-2 gap-1"
                  style={{ color: "#6c757d", background: "#EFF6FF", fontSize: 13 }}
                >
                  <FiFileText size={11} color="#9CA3AF" />
                  {partner?.registration_id || ""}
                </span>
              </div>
            </div>
            <span
              className="align-self-start px-3 py-1 rounded-pill small fw-semibold"
              style={{ color: meta.color, background: meta.bg, border: "1px solid #E5E7EB" }}
            >
              {topBadgeLabel}
            </span>
          </div>

          <div className="row mt-3 g-0 border rounded overflow-hidden">
            <div className="col-md-3 p-3 border-end">
              <div className="text-muted text-uppercase mb-1" style={{ fontSize: 11, letterSpacing: "0.03em" }}>Category</div>
              <div className="d-inline-flex px-2 py-1 rounded-2 fw-semibold" style={{ color: "#2563EB", background: "#EFF6FF", fontSize: 13 }}>
                {partner?.category || "Organisation"}
              </div>
            </div>
            <div className="col-md-3 p-3 border-end">
              <div className="text-muted text-uppercase mb-1" style={{ fontSize: 11, letterSpacing: "0.03em" }}>Category Type</div>
              <div className="fw-semibold text-dark">{partner?.sub_category || "Government Trust"}</div>
            </div>
            <div className="col-md-3 p-3 border-end">
              <div className="text-muted text-uppercase mb-1" style={{ fontSize: 11, letterSpacing: "0.03em" }}>Location</div>
              <div className="fw-semibold text-dark">{`${partner?.city || "Bhopal"}, ${partner?.partnerState?.state_name || "Madhya Pradesh"}`}</div>
            </div>
            <div className="col-md-3 p-3">
              <div className="text-muted text-uppercase mb-1" style={{ fontSize: 11, letterSpacing: "0.03em" }}>Submitted</div>
              <div className="fw-semibold text-dark">{partner?.createdAt ? moment(partner.createdAt).format("MMM D, h:mm A") : "Today, 10:24 AM"}</div>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card className="mb-3" style={{ borderRadius: 12, border: "1px solid #E5E7EB", background: "#F8F9FB", boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)" }}>
        <CardBody className="p-2 p-md-3">
          <div className="row g-3">
            <div className="col-lg-8">
              <Card className="mb-0" style={{ borderRadius: 10, border: "1px solid #E5E7EB", boxShadow: "none" }}>
                <CardBody className="p-0">
                  <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
                    <span className="fw-semibold d-inline-flex align-items-center gap-2">
                      <FiUser size={13} color="#6B7280" />
                      Contact person
                    </span>
                    <span className="badge rounded-pill" style={{ background: "#E8F8ED", color: "#2E8B57", border: "1px solid #C7EDCF" }}>
                      Both channels verified
                    </span>
                  </div>
                  <div className="p-3">
                    <table className="table table-sm mb-0" style={{ border: "1px solid #E5E7EB" }}>
                      <tbody>
                        <tr>
                          <td className="text-muted" style={{ width: "34%", padding: "10px 12px", borderColor: "#E5E7EB", fontSize: 13 }}>Full name</td>
                          <td className="fw-semibold text-dark" style={{ padding: "10px 12px", borderColor: "#E5E7EB", fontSize: 14 }}>{partner?.fullName || "Rohit Sharma"}</td>
                        </tr>
                        <tr>
                          <td className="text-muted" style={{ padding: "10px 12px", borderColor: "#E5E7EB", fontSize: 13 }}>Designation</td>
                          <td className="fw-semibold text-dark" style={{ padding: "10px 12px", borderColor: "#E5E7EB", fontSize: 14 }}>{partner?.designation || "Manager"}</td>
                        </tr>
                        <tr>
                          <td className="text-muted" style={{ padding: "10px 12px", borderColor: "#E5E7EB", fontSize: 13 }}>Mobile</td>
                          <td className="fw-semibold text-dark" style={{ padding: "10px 12px", borderColor: "#E5E7EB", fontSize: 14 }}>
                            <span className="text-primary">{partner?.phoneNumber || "+91 98765 43210"}</span>{" "}
                            <span className="badge bg-success-subtle text-success ms-2">✓ OTP verified</span>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted" style={{ padding: "10px 12px", borderColor: "#E5E7EB", fontSize: 13 }}>Email ID</td>
                          <td className="fw-semibold text-dark" style={{ padding: "10px 12px", borderColor: "#E5E7EB", fontSize: 14 }}>
                            <span className="text-primary">{partner?.email || "rohit@gmail.com"}</span>{" "}
                            <span className="badge bg-success-subtle text-success ms-2">✓ Link verified</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
              <Card className="mt-3" >
              <CardBody className="p-0">
                <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top border-bottom">
                    <span className="fw-semibold">Internal note</span>
                    <span className="badge rounded-pill" style={{ background: "#FEF9C3", color: "#A16207", border: "1px solid #FDE68A" }}>
                      Never shown to partner
                    </span>
                  </div>
                  <div className="p-3">
                    <Input
                      type="textarea"
                      rows={3}
                      value={internalNote}
                      onChange={(e) => setInternalNote(e.target.value)}
                      style={{ borderColor: "#E5E7EB", resize: "none", padding: "12px" }}
                      placeholder="Add a note about this application — visible to your admin team only..."
                    />
                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <small className="text-muted">Visible to all admin team members only</small>
                      <Button size="sm" color="light" style={{ border: "1px solid #E5E7EB", color: "#9CA3AF", minWidth: 88 }} disabled>
                        Save Note
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>

            <div className="col-lg-4">
              <Card className="mb-3" style={{ borderRadius: 10, border: "1px solid #E5E7EB", boxShadow: "none" }}>
                <CardBody className="p-0">
                  <div className="px-3 py-2 border-bottom fw-semibold text-dark">Actions</div>
                  <div className="p-3 d-grid gap-2">
                    {applicationStatus === STATUS.PENDING && (
                      <>
                        <Button color="success" className="w-100 text-start fw-semibold" onClick={() => setApproveModal(true)}>✓ Approve Application</Button>
                        <Button className="w-100 text-start fw-semibold" style={{ color: "#B45309", background: "#FFFBEB", borderColor: "#FDE68A" }} onClick={() => setInfoModal(true)}>⚠ Need More Info</Button>
                        <Button className="w-100 text-start fw-semibold" style={{ color: "#B91C1C", background: "#FEF2F2", borderColor: "#FECACA" }} onClick={() => setRejectModal(true)}>✕ Reject Application</Button>
                        <Button color="light" className="w-100 text-start fw-semibold" style={{ borderColor: "#D1D5DB", color: "#374151" }} onClick={() => setApplicationStatus(STATUS.PENDING)}>◉ Mark as Under Review</Button>
                      </>
                    )}

                    {applicationStatus === STATUS.INFO_REQUESTED && (
                      <>
                        <div className="text-center py-3">
                          <div className="fw-semibold" style={{ color: meta.color }}>{meta.label}</div>
                        </div>
                        <Button color="success" className="w-100 text-start fw-semibold" onClick={() => setApproveModal(true)}>✓ Approve Application</Button>
                        <Button className="w-100 text-start fw-semibold" style={{ color: "#B91C1C", background: "#FEF2F2", borderColor: "#FECACA" }} onClick={() => setRejectModal(true)}>✕ Reject Application</Button>
                      </>
                    )}

                    {applicationStatus === STATUS.APPROVED && (
                      <div className="text-center py-4">
                        <div className="fw-semibold" style={{ color: meta.color }}>{meta.label}</div>
                        <small className="text-muted">Credentials sent to partner</small>
                      </div>
                    )}

                    {applicationStatus === STATUS.REJECTED && (
                      <>
                        <div className="text-center py-3">
                          <div className="fw-semibold" style={{ color: meta.color }}>{meta.label}</div>
                        </div>
                        <Button color="light" className="w-100 text-start fw-semibold" style={{ color: "#6D28D9", borderColor: "#C4B5FD" }} onClick={() => setApplicationStatus(STATUS.PENDING)}>
                          ↶ Reopen Application
                        </Button>
                      </>
                    )}
                  </div>
                </CardBody>
              </Card>

              <Card style={{ borderRadius: 10, border: "1px solid #E5E7EB", boxShadow: "none" }}>
                <CardBody className="p-0">
                  <div className="px-3 py-2 border-bottom fw-semibold text-dark">Quick verification</div>
                  <div className="p-3 d-grid gap-2">
                    <a href={`https://www.google.com/search?q=${encodeURIComponent(partner?.businessName || "")}`} target="_blank" rel="noreferrer" className="text-decoration-none d-inline-flex align-items-center gap-2" style={{ color: "#2563EB" }}>
                      <FiArrowRight size={14} /> Google "{partner?.businessName || "State Museum Bhopal"}"
                    </a>
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${partner?.businessName || ""} ${partner?.city || ""}`)}`} target="_blank" rel="noreferrer" className="text-decoration-none d-inline-flex align-items-center gap-2" style={{ color: "#2563EB" }}>
                      <FiArrowRight size={14} /> View on Google Maps
                    </a>
                    <button type="button" className="btn btn-link p-0 text-start text-decoration-none d-inline-flex align-items-center gap-2" style={{ color: "#2563EB" }}>
                      <FiArrowRight size={14} /> Check duplicate applications
                    </button>
                    <button type="button" className="btn btn-link p-0 text-start text-decoration-none d-inline-flex align-items-center gap-2" style={{ color: "#2563EB" }}>
                      <FiArrowRight size={14} /> Verify contact number
                    </button>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </CardBody>
      </Card>

      <div className="row g-3">
        <div className="col-lg-8">
          <Card className="border-0" style={{ borderRadius: 12 }}>
            <CardBody className="p-0">
              <button
                type="button"
                className="btn btn-link text-decoration-none w-100 text-start d-flex justify-content-between align-items-center px-3 py-2 border-bottom"
                onClick={() => setHistoryOpen((prev) => !prev)}
              >
                <span className="fw-semibold text-dark">
                  Communication history{" "}
                  <span className="badge bg-primary-subtle text-primary">{communicationHistory.length} email{communicationHistory.length > 1 ? "s" : ""} sent</span>
                </span>
                <span className="text-muted">{historyOpen ? "▾" : "▸"}</span>
              </button>
              {historyOpen && (
                <div className="p-3">
                  {communicationHistory.map((entry) => (
                    <div key={entry.id} className="mb-2 pb-2 border-bottom">
                      <div className="fw-semibold">{entry.text}</div>
                      <small className="text-muted">To: {entry.email} · {entry.time}</small>
                    </div>
                  ))}
                  <small className="text-muted">All future emails — approval, rejection, info requests — will be auto-logged here automatically.</small>
                </div>
              )}
            </CardBody>
          </Card>
        </div>

        <div className="col-lg-4" />
      </div>

      <Modal isOpen={approveModal} toggle={() => setApproveModal(false)} centered>
        <ModalHeader toggle={() => setApproveModal(false)} style={{ borderTop: "3px solid #10B981" }}>
          Approve this application?
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">Login credentials will be auto-generated and sent to the partner immediately.</p>
          <div className="small p-2 rounded" style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1E3A8A" }}>
            <div><strong>Happens automatically:</strong></div>
            <div>Partner account created · Unique Partner ID generated</div>
            <div>Welcome email + temporary password sent to</div>
            <strong>{partner?.email || "rohit@gmail.com"}</strong>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setApproveModal(false)} disabled={saving}>Cancel</Button>
          <Button color="success" onClick={onApprove} disabled={saving}>{saving ? "Approving..." : "Approve & Send Credentials"}</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={infoModal} toggle={() => setInfoModal(false)} centered>
        <ModalHeader toggle={() => setInfoModal(false)} style={{ borderTop: "3px solid #F59E0B" }}>
          Request more information
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">An email listing what you need will be sent to <strong>{partner?.email || "rohit@gmail.com"}</strong></p>
          <label className="form-label small fw-semibold mb-1">WHAT IS NEEDED?</label>
          <Input type="select" className="mb-3" value={infoReason} onChange={(e) => setInfoReason(e.target.value)}>
            <option value="">Select what is missing...</option>
            {INFO_REQUEST_REASONS.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
          </Input>
          <label className="form-label small fw-semibold mb-1">MESSAGE TO APPLICANT (OPTIONAL)</label>
          <Input type="textarea" rows={3} value={infoMessage} onChange={(e) => setInfoMessage(e.target.value)} placeholder="Explain what you need and why..." />
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setInfoModal(false)} disabled={saving}>Cancel</Button>
          <Button style={{ background: "#F59E0B", borderColor: "#F59E0B" }} onClick={onInfoRequest} disabled={saving}>
            {saving ? "Sending..." : "Send Information Request"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={rejectModal} toggle={() => setRejectModal(false)} centered>
        <ModalHeader toggle={() => setRejectModal(false)} style={{ borderTop: "3px solid #EF4444" }}>
          Reject this application
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">A rejection email with your selected reason will be sent to <strong>{partner?.email || "rohit@gmail.com"}</strong></p>
          <label className="form-label small fw-semibold mb-1">REJECTION REASON *</label>
          <Input type="select" className="mb-3" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}>
            <option value="">Select a reason...</option>
            {REJECTION_REASONS.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
          </Input>
          <label className="form-label small fw-semibold mb-1">NOTE FOR APPLICANT (OPTIONAL)</label>
          <Input type="textarea" rows={3} value={rejectMessage} onChange={(e) => setRejectMessage(e.target.value)} placeholder="Any additional context for the applicant..." />
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setRejectModal(false)} disabled={saving}>Cancel</Button>
          <Button color="danger" onClick={onReject} disabled={saving}>{saving ? "Rejecting..." : "Reject Application"}</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PartnerApplicationDetails;
